/**
 * gui-react's brewauth groups client (jorigin/brewauth#29 phase 2).
 *
 * ONE `createBrewauthGroupsClient` instance, built from the same config
 * object as the admin client (`brewauthClientConfig`): the same same-origin
 * base URL, OIDC token getter and 401 re-auth ladder. Import it as
 * `import { brewauthGroups } from '@/api/brewauthGroups'`.
 *
 * Also re-exports `@brew/ui/groups` (types, errors, `refToActor`,
 * `groupSavePlan` helpers, and since 0.19.0 the B-types: `ThroughHop`, the
 * transitive listing's and the filter preview's), so screens import groups
 * things from one place. Every read and write, `listTransitiveMembers`,
 * `parentCandidates` and `filterPreview` included, goes through this one
 * instance. It also holds the one adapter a surviving screen still needs: `toAclActor`,
 * brewauth's `GroupView` / `MemberView` as gui-react's `AclActor`, for the
 * ACL and sharing surfaces that keep speaking Java's actor shape.
 *
 * The group avatar's IMAGE is the one thing here that is Java's: it lives in
 * Java's document store (jorigin/brewauth#115), uploaded and deleted through
 * Java's `api/groups/:id/avatar`. The group's `avatar` FIELD is brewauth's
 * (jorigin/brewauth#119 P3-3): `replaceGroupAvatar` and `removeGroupAvatar`
 * below set it with `PATCH /groups/:id`.
 *
 * There is deliberately NO `toLegacyGroup` (brewauth view -> Java `Group`):
 * every screen that read the Java `Group` type (the admin group pages, the
 * old group editor, `MyGroupsPanel`'s editor, the profile `GroupsTab`) was
 * retired or rebuilt on brewauth's own views in GB1-GB8, so nothing would
 * call it.
 */
import {
  createBrewauthGroupsClient,
  type GroupDetail,
  type GroupView,
  type MemberView,
  type PatchGroupBody,
} from '@brew/ui/groups';
import type { AclActor } from './acl';
import { brewauthClientConfig } from './brewauthAdminClient';
import apiClient from './client';
import { getAvatarUrl } from './profiles';

export * from '@brew/ui/groups';

export const brewauthGroups = createBrewauthGroupsClient(brewauthClientConfig);

/** The Java avatar path of a group, as `ActorWrapper` alone writes `$icon`. */
export function groupAvatarPath(id: string): string {
  return `api/groups/${id}/avatar`;
}

/**
 * The avatar path for a group whose `avatar` (the Java `documents` id) is
 * known, with that id as the cache-buster: every upload makes a new
 * document, so the URL changes exactly when the image does and the shared
 * image cache (`@/api/apiImage`, keyed by URL) never serves the old one.
 */
export function groupAvatarSrc(id: string, avatar: string): string {
  return `${groupAvatarPath(id)}?v=${encodeURIComponent(avatar)}`;
}

/**
 * The file name every group avatar upload carries. Java's upload checks the
 * name only, case-sensitively (`endsWith("jpeg")`/`endsWith("png")`, else
 * 415), so the browser always re-encodes to a PNG and names it this.
 */
export const GROUP_AVATAR_FILENAME = 'avatar.png';

/**
 * The avatar half of brewauth's `PATCH /groups/:id` (jorigin/brewauth#119
 * P3-1): `@brew/ui` 0.19.0's `PatchGroupBody` predates the field. `null`
 * removes the avatar.
 */
type AvatarPatch = PatchGroupBody & { avatar: string | null };

/**
 * Store an avatar image in Java's document store: `POST api/groups/:id/avatar`
 * (Java's `update` role). Since jorigin/brewauth#119 P3-3 Java writes ONLY the
 * document -- namespace the group's id, label `.avatar`, readable by Everyone,
 * the shape brewauth accepts -- and never the group, so the group's ETag does
 * not move. Answers the document's id. `png` is the cropped export, always
 * sent as `avatar.png`, `image/png`.
 */
export async function uploadGroupAvatarDocument(id: string, png: Blob): Promise<string> {
  const form = new FormData();
  form.append('file', new File([png], GROUP_AVATAR_FILENAME, { type: 'image/png' }));
  const res = await apiClient.post(groupAvatarPath(id), form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  const doc = (res.data as { _id?: unknown } | undefined)?._id;
  if (typeof doc !== 'string' || doc === '') {
    throw new Error(`Java's avatar upload for group ${id} answered no document id`);
  }
  return doc;
}

/** Point the group at an avatar document, or (`null`) at none: brewauth's `PATCH /groups/:id`, under `If-Match`. */
export function setGroupAvatar(
  id: string,
  etag: string,
  avatar: string | null,
): Promise<GroupDetail> {
  const patch: AvatarPatch = { avatar };
  return brewauthGroups.patchGroup(id, etag, patch);
}

/**
 * Delete the group's avatar images its stored `avatar` no longer names:
 * Java's `DELETE api/groups/:id/avatar`, which since jorigin/brewauth#119 P3-3
 * never writes the group. It needs Java's `delete` role, so call it only with
 * DELETE.
 */
export async function deleteUnusedGroupAvatars(id: string): Promise<void> {
  await apiClient.delete(groupAvatarPath(id));
}

/** After the write the person asked for has landed: an image left behind is named by nothing, so report it and go on. */
async function cleanUpUnusedAvatars(id: string): Promise<void> {
  try {
    await deleteUnusedGroupAvatars(id);
  } catch (err) {
    console.warn(`group ${id}: an avatar image the group no longer uses was not deleted`, err);
  }
}

/**
 * Upload or replace (jorigin/brewauth#119 P3-3): Java stores the image, then
 * brewauth points the group at it under the group's ETag, then -- with DELETE
 * (`cleanUp`) -- Java deletes what the group no longer names: the old image,
 * or, when the PATCH was refused, the new one. Without DELETE the old image
 * stays in the document store, named by nothing, until someone holding
 * DELETE next writes the avatar. A refused PATCH is rethrown.
 */
export async function replaceGroupAvatar(
  group: Pick<GroupDetail, 'id' | 'etag'>,
  png: Blob,
  cleanUp: boolean,
): Promise<void> {
  const doc = await uploadGroupAvatarDocument(group.id, png);
  try {
    await setGroupAvatar(group.id, group.etag, doc);
  } catch (err) {
    if (cleanUp) await cleanUpUnusedAvatars(group.id);
    throw err;
  }
  if (cleanUp) await cleanUpUnusedAvatars(group.id);
}

/**
 * Remove (jorigin/brewauth#119 P3-3): brewauth clears the field first, then
 * Java deletes the image. Offered only with DELETE, which Java's delete needs.
 */
export async function removeGroupAvatar(group: Pick<GroupDetail, 'id' | 'etag'>): Promise<void> {
  await setGroupAvatar(group.id, group.etag, null);
  await cleanUpUnusedAvatars(group.id);
}

function isGroupView(v: GroupView | MemberView): v is GroupView {
  return 'namespace' in v;
}

/**
 * A brewauth `GroupView` or `MemberView` as gui-react's `AclActor` (the
 * shape Java's actor search returns and every ACL/sharing screen holds).
 *
 * The avatar follows the rule gui-react already uses (`aclActorAvatarUrl`):
 *  - a group VIEW knows whether it has an avatar (`avatar`, the Java
 *    documents id, or `null`), so it gets `$icon` only when it has one and
 *    carries `avatar: null` otherwise, the single-group shape `GroupsTab`
 *    already reads: known to have none, so no request is made;
 *    (#115: that `$icon` carries the avatar id as `?v=`, so a replaced
 *    avatar is a new URL and the shared image cache refetches it);
 *  - a MEMBER row does not say, so it gets `$icon` exactly as Java's
 *    `ActorWrapper` writes it for every actor (the endpoint redirects to a
 *    blank default when there is none, which `ApiImage` reports as a failure
 *    and the avatar then falls back to initials).
 *
 * A group's `isSystem` is NOT mapped to `$isSystem`: in Java's actor JSON
 * `$isSystem` means a SYSTEM profile (`ActorWrapper` never sets it on a
 * group), and the ACL pickers drop `$isSystem` rows. The only sys group a
 * picker is ever offered is Everyone (the seeded sys filter group,
 * `filter: ""`): Java's search offered no other, even to administrators
 * (`GroupsResource.postSearchFilter` returns `item.isFilter()` for a sys
 * group), and brewauth's `visibility=acl` leaves the rest out server-side
 * (`groupquery.go`'s `$nor`). Everyone IS `isSystem`, so mapping the flag
 * would drop Everyone from every picker.
 */
export function toAclActor(v: GroupView | MemberView): AclActor {
  if (isGroupView(v)) {
    return {
      _id: v.id,
      $reference: v.ref,
      $type: 'group',
      // Versioned by the avatar id (#115), so a replaced avatar is refetched.
      ...(v.avatar ? { $icon: groupAvatarSrc(v.id, v.avatar) } : {}),
      avatar: v.avatar,
      metadata: {
        label: v.capco ? { value: v.label, capco: v.capco } : { value: v.label },
        namespace: v.namespace,
        desc: { value: v.desc },
      },
      ...(v.filter !== null ? { filter: v.filter } : {}),
    };
  }
  const group = v.kind === 'group';
  return {
    _id: v.id,
    $reference: v.ref,
    $type: group ? 'group' : 'individual',
    $icon: group ? groupAvatarPath(v.id) : getAvatarUrl(v.id),
    metadata: { label: { value: v.label } },
  };
}
